import type {
  AIStreamDataChunk,
  AIStreamDataChunkContent,
  AIStreamDataChunkError,
  AIStreamDataChunkRephrasingQueries,
  AIStreamDataChunkSources,
  AIStreamDataChunkSteps,
  AIStreamDataChunkTitleGeneration,
  AIStreamDataSourceDetails,
} from '~/features-swym/aura/interfaces/stream-data'

import { RTCTextContentDataType } from '~/features-shared/rtconv/interfaces/rtconversation'

/**
 * Type guard to check for the presence of a source chunk.
 * @param data Stream data (chunk).
 * @returns true if the chunk is of type sources.
 */
export function isSources(data: AIStreamDataChunk): data is AIStreamDataChunkSources {
  const dataSources = data as AIStreamDataChunkSources

  if (Object.prototype.hasOwnProperty.call(dataSources, 'hits'))
    return true
  else
    return false
}

/**
 * Type guard to check for the presence of a content chunk.
 * @param data Stream data (chunk).
 * @returns true if the chunk is of type content.
 */
export function isContent(data: AIStreamDataChunk): data is AIStreamDataChunkContent {
  const dataContent = data as AIStreamDataChunkContent

  if (dataContent?.choices?.[0]?.delta?.content)
    return true
  else
    return false
}

/**
 * Type guard to check for the presence of an error chunk.
 * @param data Stream data (chunk).
 * @returns true if the chunk is of type error.
 */
export function isError(data: AIStreamDataChunk): data is AIStreamDataChunkError {
  const dataError = data as AIStreamDataChunkError

  if (dataError.code || dataError.error)
    return true
  else
    return false
}

/**
 * Type guard to check for the presence of a title generation chunk.
 * @param data Stream data (chunk).
 * @returns true if the chunk contains a generated title.
 */
export function isTitleGeneration(data: AIStreamDataChunk): data is AIStreamDataChunkTitleGeneration {
  const dataTitleGeneration = data as AIStreamDataChunkTitleGeneration

  if (dataTitleGeneration.type === 'title_generation' && dataTitleGeneration.title)
    return true
  else
    return false
}

/**
 * Type guard to check for the presence of a AIAI steps chunk.
 * @param data Stream data (chunk).
 * @returns true if the chunk contains the AIAI steps.
 */
export function isSteps(data: AIStreamDataChunk): data is AIStreamDataChunkSteps {
  const dataContent = data as AIStreamDataChunkSteps
  if (dataContent.type === 'steps')
    return true
  else
    return false
}

/**
 * Type guard to check for the presence of a rephrasing chunk.
 * @param data Stream data (chunk).
 * @returns true if the chunk is of type rephrasing.
 */
export function isRephrasingQueries(data: AIStreamDataChunk): data is AIStreamDataChunkRephrasingQueries {
  const dataContent = data as AIStreamDataChunkRephrasingQueries
  if (dataContent.type === RTCTextContentDataType.rephrasing)
    return true
  else
    return false
}

/**
 * Type guard to check if the source is coming from the Platform.
 * @param data Source's details.
 * @returns true if it is a Platform source.
 */
export function isPlatformSource(data: AIStreamDataSourceDetails): boolean {
  return !!('x3dcontent__content__id' in data && data.x3dcontent__content__id)
    || !!('x3dcontent__content__i3dx' in data && data.x3dcontent__content__i3dx)
}

/**
 * Type guard to check if the source is external.
 * @param data Source's details.
 * @returns true if it is an external source.
 */
export function isExternalSource(data: AIStreamDataSourceDetails): boolean {
  return !!('content__url' in data && data.content__url)
    && !!('content__title' in data && data.content__title)
}
